import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WACHTWOORD_MIN_LENGTE, keurWachtwoord } from '@vip/shared';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Logo } from '../../design-system';
import { ApiError } from '../../lib/apiClient';
import { authApi } from './auth.api';
import { useAuth } from './useAuth';
import { WachtwoordMeter } from './WachtwoordMeter';

/**
 * Het scherm achter de uitnodigingslink uit de mail. Publiek bereikbaar, want
 * wie hier komt heeft nog geen account. Zelfde opzet als het inlogscherm, tot in
 * de breedte van de kaart en de uitlijning, zodat het niet aanvoelt als een
 * andere applicatie. Die uitlijning: het merk in het midden boven de kaart, de
 * koppen erin links. `CardHeader` lijnt zelf links uit; het centreren daarvan
 * kwam uit `login-03`, een block met aanmeldknoppen en een onderschrift onder de
 * kop die hier geen van beide bestaan.
 */
export function AccountAanmakenPage() {
  const { token = '' } = useParams();
  const { maakAccount } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState<string | null>(null);
  const [linkFout, setLinkFout] = useState('');
  const [laden, setLaden] = useState(true);

  const [naam, setNaam] = useState('');
  const [wachtwoord, setWachtwoord] = useState('');
  const [herhaling, setHerhaling] = useState('');
  const [fouten, setFouten] = useState<string[]>([]);
  const [bezig, setBezig] = useState(false);

  // De link eerst laten keuren door de server: verlopen of al gebruikt moet je
  // meteen zien, niet pas nadat je een wachtwoord hebt zitten bedenken.
  useEffect(() => {
    let actueel = true;
    authApi
      .checkUitnodiging(token)
      .then((r) => actueel && setEmail(r.email))
      .catch(
        (err) =>
          actueel &&
          setLinkFout(
            err instanceof ApiError ? err.message : 'Deze link kon niet worden gecontroleerd.',
          ),
      )
      .finally(() => actueel && setLaden(false));
    return () => {
      actueel = false;
    };
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFouten([]);

    // Dezelfde regels als de server, zodat je niet hoeft te wachten op een
    // rondje netwerk voor iets dat hier al te zien is.
    const lokaal = keurWachtwoord(wachtwoord, { email: email ?? '', naam });
    if (wachtwoord !== herhaling) lokaal.push('De twee wachtwoorden zijn niet gelijk.');
    if (lokaal.length) return setFouten(lokaal);

    setBezig(true);
    try {
      await maakAccount(token, { naam, wachtwoord });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // De server stuurt zijn bezwaren als lijst mee; die zijn preciezer dan
        // de samengevatte melding.
        const details = (err.details as { fouten?: string[] } | undefined)?.fouten;
        setFouten(details?.length ? details : [err.message]);
      } else {
        setFouten(['Er ging iets mis. Probeer het opnieuw.']);
      }
    } finally {
      setBezig(false);
    }
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        {/* Het merk gecentreerd boven de kaart; de kop erin links. */}
        <span className="self-center">
          <Logo height={40} />
        </span>

        {laden ? (
          <Card>
            <CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner />
              Even kijken of deze link nog geldig is…
            </CardContent>
          </Card>
        ) : linkFout ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Deze link werkt niet meer.</CardTitle>
              <CardDescription>{linkFout}</CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Vraag degene die je heeft uitgenodigd om een nieuwe uitnodiging. Een link is één dag
              geldig en kan één keer gebruikt worden.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Welkom aan boord.</CardTitle>
              <CardDescription>
                Je maakt een account voor <strong>{email}</strong>. Kies een wachtwoord, daarna ben
                je meteen ingelogd.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit}>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="naam">Je naam</FieldLabel>
                    <Input
                      autoComplete="name"
                      id="naam"
                      onChange={(e) => setNaam(e.target.value)}
                      placeholder="Voor- en achternaam"
                      required
                      value={naam}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="wachtwoord">Wachtwoord</FieldLabel>
                    <Input
                      autoComplete="new-password"
                      id="wachtwoord"
                      onChange={(e) => setWachtwoord(e.target.value)}
                      required
                      type="password"
                      value={wachtwoord}
                    />
                    <FieldDescription>Minimaal {WACHTWOORD_MIN_LENGTE} tekens.</FieldDescription>
                    <WachtwoordMeter eigenWoorden={[naam, email ?? '']} wachtwoord={wachtwoord} />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="herhaling">Wachtwoord nog een keer</FieldLabel>
                    <Input
                      autoComplete="new-password"
                      id="herhaling"
                      onChange={(e) => setHerhaling(e.target.value)}
                      required
                      type="password"
                      value={herhaling}
                    />
                  </Field>

                  {fouten.length > 0 && (
                    <Alert variant="destructive">
                      <AlertTitle>
                        {fouten.map((f) => (
                          <div key={f}>{f}</div>
                        ))}
                      </AlertTitle>
                    </Alert>
                  )}

                  <Button className="w-full" disabled={bezig} type="submit">
                    {bezig && <Spinner data-icon="inline-start" />}
                    {bezig ? 'Bezig…' : 'Account aanmaken'}
                  </Button>
                </FieldGroup>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
